import type React from "react";
import { useEffect, useState, useCallback } from "react";
import DataTable, { type TableColumn } from "react-data-table-component";
import { useFilter } from "../../../../context/FilterContext";
import { Eye, ExternalLink, Download } from "lucide-react";
import useApiHook from "../../../../utils/useApi";
import TransactionDetailsModal from "./TransactionDetailsModal";
import TransactionLogsModal from "./TransactionLogsModal";
import {
  ApiTransactionResponse,
  TransactionData,
} from "../../../../interface/interfaces";
import { formatCurrency } from "../../../../utils/utils";
import { customStyles_T } from "../../../../utils/table_utils";
import Loading from "../../loading/Loading";

const Transactions: React.FC = () => {
  const { startDate, endDate, type, searchQuery } = useFilter();

  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState<boolean>(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<TransactionData | null>(null);

  const [isLogsModalOpen, setIsLogsModalOpen] = useState<boolean>(false);
  const [selectedReference, setSelectedReference] = useState<string>("");

  const [isExporting, setIsExporting] = useState<boolean>(false);

  const { data, isLoading, makeRequest } = useApiHook<ApiTransactionResponse>(
    "/dashboard/payments",
    {
      method: "get",
    }
  );

  const handleViewLog = (reference: string) => {
    setSelectedReference(reference);
    setIsLogsModalOpen(true);
  };

  const handleViewDetails = (transaction: TransactionData) => {
    setSelectedTransaction(transaction);
    setIsDetailsModalOpen(true);
  };

  // Export to CSV function
  const exportToCSV = () => {
    if (!data?.data?.data || data.data.data.length === 0) {
      return;
    }

    setIsExporting(true);

    try {
      const filteredData = data.data.data.map((transaction) => {
        const {
          redirectUrl,
          requestId,
          paymentChannelId,
          customerId,
          merchantId,
          ...filteredTransaction
        } = transaction;
        return filteredTransaction;
      });

      const headers = Object.keys(filteredData[0]);
      const csvHeader = headers.join(",");

      const csvRows = filteredData.map((row) => {
        return headers
          .map((header) => {
            const value = row[header as keyof typeof row];

            if (typeof value === "string" && value.includes(",")) {
              return `"${value}"`;
            }
            return value;
          })
          .join(",");
      });

      // Combine header and rows
      const csvContent = [csvHeader, ...csvRows].join("\n");

      // Create a blob and download link
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `transactions_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      setHasError(true);
      setErrorMessage("Failed to export transactions. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  // Define table columns
  const columns: TableColumn<TransactionData>[] = [
    {
      name: "Reference",
      selector: (row) => row.reference,
      sortable: true,
      cell: (row) => (
        <div className="font-medium text-gray-900">{row.reference}</div>
      ),
    },
    {
      name: "Amount",
      selector: (row) => row.amount,
      sortable: true,
      cell: (row) => (
        <div className="font-medium">
          {formatCurrency(row.amount, row.currency)}
        </div>
      ),
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => (
        <div
          className={`px-2.5 py-1 rounded-full text-xs font-medium ${
            row.status === "SUCCESSFUL"
              ? "bg-green-100 text-green-800"
              : row.status === "FAILED"
              ? "bg-red-100 text-red-800"
              : "bg-yellow-100 text-yellow-800"
          }`}
        >
          {row.status}
        </div>
      ),
    },
    {
      name: "Narration",
      selector: (row) => row.narration,
      sortable: true,
      wrap: true,
    },
    {
      name: "Date Created",
      selector: (row) => row.dateCreated ?? "",
      sortable: true,
      cell: (row) => {
        const formatDate = (dateString: string) => {
          const date = new Date(dateString);
          return {
            date: date.toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "2-digit",
            }),
            time: date.toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            }),
          };
        };

        const { date, time } = formatDate(row.dateCreated ?? "");

        return (
          <div className="text-sm">
            <div className="font-medium text-gray-900">{date}</div>
            <div className="text-gray-500 text-xs">{time}</div>
          </div>
        );
      },
      width: "140px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleViewLog(row.reference)}
            className="flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
          >
            <Eye size={16} className="mr-1.5" />
            View Log
          </button>
          <button
            onClick={() => handleViewDetails(row)}
            className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
          >
            <ExternalLink size={16} className="mr-1.5" />
            View More
          </button>
        </div>
      ),
      button: true,
      width: "400px",
    },
  ];

  const fetchTransactions = useCallback(async () => {
    setHasError(false);
    setErrorMessage("");

    const params = {
      page,
      size: perPage,
      search: searchQuery || "",
      startDate: startDate || "",
      endDate: endDate || "",
      type: type || "",
    };

    const response = await makeRequest(params);

    // Check for API errors
    if (response?.data?.statusCode !== "00") {
      setHasError(true);
      setErrorMessage(response?.data?.description || "An error occurred");
      setTotalRows(0);
    } else if (response?.data?.data) {
      setTotalRows(response.data.data.totalCount || 0);
    }
  }, [page, perPage, searchQuery, startDate, endDate, type]);

  // Fetch transactions when filters or pagination changes
  useEffect(() => {
    fetchTransactions();
  }, [startDate, endDate, type, searchQuery]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setPage(page);
  };

  // Handle rows per page change
  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  return (
    <section className="bg-gray-50 p-4 md:p-6 rounded-lg">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Transactions</h1>
        </div>
        <button
          onClick={exportToCSV}
          disabled={isExporting || isLoading || !data?.data?.data?.length}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isExporting ? (
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
          ) : (
            <Download size={18} className="mr-2" />
          )}
          Export CSV
        </button>
      </div>

      {hasError ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
          <p className="font-medium">Error</p>
          <p className="text-sm">{errorMessage}</p>
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={data?.data?.data || []}
          progressPending={isLoading}
          progressComponent={<Loading />}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          highlightOnHover
          responsive
          persistTableHead
          customStyles={customStyles_T}
          noDataComponent={
            <div className="p-6 text-center text-gray-500">
              No transactions found
            </div>
          }
        />
      )}

      {/* Transaction Details Modal */}
      <TransactionDetailsModal
        transaction={selectedTransaction}
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
      />

      {/* Transaction Logs Modal */}
      <TransactionLogsModal
        reference={selectedReference}
        isOpen={isLogsModalOpen}
        onClose={() => setIsLogsModalOpen(false)}
      />
    </section>
  );
};

export default Transactions;
